<template>
  <div class="h-full overflow-y-auto w-full flex flex-col">
    <!-- Main Dashboard Grid -->
    <div class="grid grid-cols-3 gap-2 mb-2">
      <!-- Contract Status Card -->
      <div class="h-full">
        <div class="flex flex-col bg-white rounded-xl shadow-sm h-full">
          <div class="bg-gradient-to-r from-ccm-blue-100 to-ccm-blue-200 px-6 py-1.5 rounded-t-xl">
            <div class="flex items-center justify-between">
              <h2 class="font-semibold text-white">
                {{ $t('HeadContract.Dashboard.contractStatus') }}
              </h2>

              <div class="w-7 h-7 bg-white/20 rounded-full flex items-center justify-center">
                <svg xmlns="http://www.w3.org/2000/svg" fill="none" stroke="currentColor" viewBox="0 0 24 24"  class="w-5 h-5 text-white">
    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
</svg>
              </div>
            </div>
          </div>

          <div class="p-6 flex justify-center h-full">
            <Transition
              enter-active-class="animate__animated animate__fadeIn animate__fast"
              mode="out-in"
            >
              <table
                v-if="project"
                class="text-sm w-full"
              >
                <tbody class="child:border-b child:border-gray-100 last-child:border-b-0">
                  <tr class="child:py-1">
                    <td class="font-medium text-gray-600">
                      {{ $t('HeadContract.Dashboard.startDate') }}
                    </td>

                    <td class="text-right font-semibold text-gray-900">
                      {{ project.contract_start_date ? project.contractStartDate.toLocaleDateString() : $t('HeadContract.Dashboard.notSet') }}
                    </td>
                  </tr>

                  <tr class="child:py-1">
                    <td class="text-sm font-medium text-gray-600">
                      {{ $t('HeadContract.Dashboard.endDate') }}
                    </td>

                    <td class="text-right text-sm font-semibold text-gray-900">
                      {{ project.contract_end_date ? project.contractEndDate.toLocaleDateString() : $t('HeadContract.Dashboard.notSet') }}
                    </td>
                  </tr>

                  <tr class="child:py-1">
                    <td class="text-sm font-medium text-gray-600">
                      {{ $t('HeadContract.Dashboard.duration') }}
                    </td>

                    <td class="text-right text-sm font-semibold text-gray-900">
                      {{ project.contract_start_date && project.contract_end_date ? `${project.contractLengthInWeeks} ${$t('HeadContract.Dashboard.weeks')}` : $t('HeadContract.Dashboard.notSet') }}
                    </td>
                  </tr>

                  <tr class="child:py-1">
                    <td class="text-sm font-medium text-gray-600">
                      {{ $t('HeadContract.Dashboard.approvedDelays') }}
                      <span class="ml-1 h-5 w-5 px-2 py-1 bg-red-100 text-red-800 text-xs rounded-full">{{ approvedDelays.length }}</span>
                    </td>

                    <td class="text-right text-sm font-semibold text-green-600">
                      {{ approvedDelaysTotalDays }} {{ $t('HeadContract.Dashboard.days') }}
                    </td>
                  </tr>

                  <tr class="child:py-1">
                    <td class="text-sm font-medium text-gray-600">
                      {{ $t('HeadContract.Dashboard.approvedContractEndDate') }}
                    </td>

                    <td class="text-right text-sm font-semibold text-blue-600">
                      {{ project.contract_end_date ? project.estimateContractEndDate(approvedDelaysTotalDays).toJSDate().toLocaleDateString() : $t('HeadContract.Dashboard.notSet') }}
                    </td>
                  </tr>

                  <tr class="child:py-1">
                    <td class="text-sm font-medium text-gray-600">
                      {{ $t('HeadContract.Dashboard.contractValue') }}
                    </td>

                    <td class="text-right text-sm font-semibold text-red-600">
                      {{ $tcc(totalBudget) }}
                    </td>
                  </tr>

                  <tr class="child:py-1">
                    <td class="text-sm font-medium text-gray-600">
                      {{ $t('HeadContract.Dashboard.approvedVariations') }}
                      <span class="ml-1 h-5 w-5 px-2 py-1 bg-red-100 text-red-800 text-xs rounded-full">{{ approvedVariationOrders.length }}</span>
                    </td>

                    <td class="text-right text-sm font-semibold text-red-600">
                      {{ $tcc(variationOrdersTotalApproved) }}
                    </td>
                  </tr>

                  <tr class="child:py-1">
                    <td class="text-sm font-medium text-gray-600">
                      {{ $t('HeadContract.Dashboard.finalAccount') }}
                    </td>

                    <td class="text-right text-sm font-semibold text-blue-600">
                      {{ $tcc(totalBudget + variationOrdersTotalApproved) }}
                    </td>
                  </tr>

                  <tr class="child:py-1">
                    <td class="text-sm font-medium text-gray-600">
                      {{ $t('HeadContract.Dashboard.paymentsCertified') }}
                    </td>

                    <td class="text-right text-sm font-semibold text-orange-600">
                      {{ $tcc(claimCertifiedTotal) }}
                    </td>
                  </tr>

                  <tr
                    v-if="project?.head_contract_settings?.performance_security === 'retention'"
                    class="child:py-1"
                  >
                    <td class="text-sm font-medium text-gray-600">
                      {{ $t('HeadContract.Dashboard.claimedRetention') }}
                    </td>

                    <td class="text-right text-sm font-semibold text-gray-900">
                      {{ $tcc(totalRetention) }}
                    </td>
                  </tr>

                  <tr
                    v-if="project?.head_contract_settings?.performance_security === 'retention'"
                    class="child:py-1"
                  >
                    <td class="text-sm font-medium text-gray-600">
                      Max Retention
                    </td>

                    <td class="text-right text-sm font-semibold text-gray-900">
                      {{ $tcc(totalBudget * (project.head_contract_settings.total_retention_percent / 100)) }}
                    </td>
                  </tr>
                </tbody>
              </table>
            </Transition>
          </div>
        </div>
      </div>

      <!-- Indirect/Development Costs Card -->
      <div
        v-if="indirectTradesTotalBudget > 0"
        class="h-full"
      >
        <div class="flex flex-col bg-white rounded-xl shadow-sm h-full">
          <div class="bg-gradient-to-r from-ccm-blue-100 to-ccm-blue-200 px-6 py-1.5 rounded-t-xl">
            <div class="flex items-center justify-between">
              <h2 class="font-semibold text-white">
                {{ $t('HeadContract.Dashboard.developmentCosts') }}
              </h2>

              <div class="w-7 h-7 bg-white/20 rounded-full flex items-center justify-center">
                <svg xmlns="http://www.w3.org/2000/svg" fill="none" stroke="currentColor" viewBox="0 0 24 24" class="w-5 h-5 text-white">
    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M11 3.055A9.001 9.001 0 1020.945 13H11V3.055z" />
    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M20.488 9H15V3.512A9.025 9.025 0 0120.488 9z" />
</svg>
              </div>
            </div>
          </div>

          <div class="p-6 flex flex-col h-full">
            <Transition
              enter-active-class="animate__animated animate__fadeIn animate__fast"
              mode="out-in"
            >
              <div
                v-if="indirectTradesTotalBudget > 0"
                class="flex flex-col h-full"
              >
                <div class="flex items-center justify-between mb-4">
                  <div class="flex items-center space-x-4">
                    <div class="flex items-center space-x-2">
                      <div class="w-3 h-3 bg-blue-500 rounded-full" />

                      <span class="text-sm text-gray-600 font-medium">{{ $t('HeadContract.Dashboard.certified') }}</span>
                    </div>

                    <div class="flex items-center space-x-2">
                      <div class="w-3 h-3 bg-red-500 rounded-full" />

                      <span class="text-sm text-gray-600 font-medium">{{ $t('HeadContract.Dashboard.remaining') }}</span>
                    </div>
                  </div>
                </div>

                <IndirectCostsPieChart
                  :total-budget="indirectTradesTotalBudget"
                  :total-certified="indirectTradesTotalCertified"
                />

                <!-- Summary Stats -->
                <div class="mt-auto">
                  <div class="flex justify-between items-center py-2 border-b border-gray-100">
                    <span class="text-sm font-medium text-gray-600">
                      {{ $t('HeadContract.Dashboard.indirectTradesTotalCertified') }}
                    </span>

                    <span class="text-sm font-semibold text-blue-600">
                      {{ australianCurrency(indirectTradesTotalCertified) }}
                    </span>
                  </div>

                  <div class="flex justify-between items-center py-2 ">
                    <span class="text-sm font-medium text-gray-600">
                      {{ $t('HeadContract.Dashboard.indirectTradesTotalBudget') }}
                    </span>

                    <span class="text-sm font-semibold text-red-600">
                      {{ australianCurrency(indirectTradesTotalBudget) }}
                    </span>
                  </div>
                </div>
              </div>
            </Transition>
          </div>
        </div>
      </div>

      <!-- Work Breakdown Card -->
      <div
        class="h-full"
        :class="{ 'lg:col-span-2': indirectTradesTotalBudget === 0 }"
      >
        <div class="bg-white rounded-xl shadow-sm">
          <div class="rounded-t-xl bg-gradient-to-r from-ccm-blue-100 to-ccm-blue-200 px-6 py-1.5">
            <div class="flex items-center justify-between">
              <h2 class="text font-semibold text-white">
                {{ $t('HeadContract.Dashboard.workBreakdown') }}
              </h2>

              <div class="w-7 h-7 bg-white/20 rounded-full flex items-center justify-center">
                <svg xmlns="http://www.w3.org/2000/svg" fill="none" stroke="currentColor" viewBox="0 0 24 24" class="w-5 h-5 text-white">
    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M11 3.055A9.001 9.001 0 1020.945 13H11V3.055z" />
    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M20.488 9H15V3.512A9.025 9.025 0 0120.488 9z" />
</svg>
              </div>
            </div>
          </div>

          <div class="p-2">
            <div class="flex items-center justify-between mb-6">
              <div class="flex items-center space-x-4 px-2">
                <div class="flex items-center space-x-2">
                  <div class="w-3 h-3 bg-blue-500 rounded-full" />

                  <span class="text-sm text-gray-600 font-medium">{{ $t('HeadContract.Dashboard.workDone') }}</span>
                </div>

                <div class="flex items-center space-x-2">
                  <div class="w-3 h-3 bg-red-500 rounded-full" />

                  <span class="text-sm text-gray-600 font-medium">{{ $t('HeadContract.Dashboard.remaining') }}</span>
                </div>
              </div>

              <div class="text-right">
                <div class="text font-bold text-gray-900">
                  {{ $tcc(currentCertifiedAmount) }}
                </div>

                <div class="text-xs text-gray-500">
                  {{ $t('HeadContract.Dashboard.of') }} {{ $tcc(totalBudget + variationOrdersTotalApproved) }}
                </div>
              </div>
            </div>

            <WorkBreakdownPieChart
              :approved-variations-budget="variationOrdersTotalApproved"
              :cumulative-amounts="monthlyCertifiedCumulative"
              :hovered-index="hoveredIndex"
              :total-budget="totalBudget"
            />

            <!-- Progress Stats -->
            <div class="mt-6 grid grid-cols-2 gap-4">
              <div class="bg-blue-50 rounded-lg p-4">
                <div class="text-sm text-blue-600 font-medium">
                  {{ $t('HeadContract.Dashboard.completion') }}
                </div>

                <div class="text-xl font-bold text-blue-900">
                  {{ totalBudget + variationOrdersTotalApproved > 0 ? ((currentCertifiedAmount / (totalBudget + variationOrdersTotalApproved)) * 100).toFixed(1) : '0' }}%
                </div>
              </div>

              <div class="bg-red-50 rounded-lg p-4">
                <div class="text-sm text-red-600 font-medium">
                  {{ $t('HeadContract.Dashboard.remaining') }}
                </div>

                <div class="text-xl font-bold text-red-900">
                  {{ $tcc(Math.max(totalBudget + variationOrdersTotalApproved - currentCertifiedAmount, 0)) }}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Progress Chart Section -->
    <div class="flex flex-col bg-white rounded-xl shadow-sm h-full">
      <div class="bg-gradient-to-r from-ccm-blue-100 to-ccm-blue-200 px-6 py-1.5 rounded-t-xl">
        <div class="flex items-center justify-between">
          <h2 class="font-semibold text-white">
            {{ $t('HeadContract.Dashboard.projectProgressTimeline') }}
          </h2>

          <div class="w-7 h-7 bg-white/20 rounded-full flex items-center justify-center">
            <svg xmlns="http://www.w3.org/2000/svg" fill="none" stroke="currentColor" viewBox="0 0 24 24" class="w-5 h-5 text-white">
    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M7 12l3-3 3 3 4-4M8 21l4-4 4 4M3 4h18M4 4h16v12a1 1 0 01-1 1H5a1 1 0 01-1-1V4z" />
</svg>
          </div>
        </div>
      </div>

      <div class="p-2 h-full">
        <ProjectProgressTimelineGraph
          v-if="project && project.contract_start_date && project.contract_end_date"
          :project="project"
          :monthly-certified-cumulative="monthlyCertifiedCumulative"
          :baseline-cumulative="baselineCumulative"
          :forecast-cumulative="forecastCumulative"
          :extended-chart-months="extendedChartMonths"
          @hover="handleTimelineHover"
          @mouseleave="handleTimelineHover(undefined)"
        />

        <div
          v-else
          class="h-full flex flex-col items-center justify-center"
        >
          <div class="w-24 h-24 bg-gray-100 rounded-full flex items-center justify-center mb-6">
            <svg fill="none" stroke="currentColor" viewBox="0 0 24 24"  class="w-12 h-12 text-gray-400">
    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
</svg>
          </div>

          <h3 class="text-lg font-semibold text-gray-900 mb-2">
            {{ $t('HeadContract.Dashboard.contractSetupRequired') }}
          </h3>

          <p class="text-gray-600 text-center mb-6 max-w-md">
            {{ $t('HeadContract.Dashboard.contractSetupDescription') }}
          </p>

          <RouterLink
            v-if="project && hasSettingsAccess"
            :to="{
              name: 'ProjectSettings', params: { projectId: project.id }, hash: '#head_contract'
            }"
            class="inline-flex items-center px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 transition-colors duration-200"
          >
            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="w-4 h-4 mr-2">
  <path stroke-linecap="round" stroke-linejoin="round" d="M9.594 3.94c.09-.542.56-.94 1.11-.94h2.593c.55 0 1.02.398 1.11.94l.213 1.281c.063.374.313.686.645.87.074.04.147.083.22.127.325.196.72.257 1.075.124l1.217-.456a1.125 1.125 0 0 1 1.37.49l1.296 2.247a1.125 1.125 0 0 1-.26 1.431l-1.003.827c-.293.241-.438.613-.43.992a7.723 7.723 0 0 1 0 .255c-.008.378.137.75.43.991l1.004.827c.424.35.534.955.26 1.43l-1.298 2.247a1.125 1.125 0 0 1-1.369.491l-1.217-.456c-.355-.133-.75-.072-1.076.124a6.47 6.47 0 0 1-.22.128c-.331.183-.581.495-.644.869l-.213 1.281c-.09.543-.56.94-1.11.94h-2.594c-.55 0-1.019-.398-1.11-.94l-.213-1.281c-.062-.374-.312-.686-.644-.87a6.52 6.52 0 0 1-.22-.127c-.325-.196-.72-.257-1.076-.124l-1.217.456a1.125 1.125 0 0 1-1.369-.49l-1.297-2.247a1.125 1.125 0 0 1 .26-1.431l1.004-.827c.292-.24.437-.613.43-.991a6.932 6.932 0 0 1 0-.255c.007-.38-.138-.751-.43-.992l-1.004-.827a1.125 1.125 0 0 1-.26-1.43l1.297-2.247a1.125 1.125 0 0 1 1.37-.491l1.216.456c.356.133.751.072 1.076-.124.072-.044.146-.086.22-.128.332-.183.582-.495.644-.869l.214-1.28Z" />
  <path stroke-linecap="round" stroke-linejoin="round" d="M15 12a3 3 0 1 1-6 0 3 3 0 0 1 6 0Z" />
</svg>
            {{ $t('HeadContract.Dashboard.configureContractSettings') }}
          </RouterLink>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import IndirectCostsPieChart from '@/components/HeadContract/Dashboard/IndirectCostsPieChart.vue';
import ProjectProgressTimelineGraph from '@/components/HeadContract/Dashboard/ProjectProgressTimelineGraph.vue';
import WorkBreakdownPieChart from '@/components/HeadContract/Dashboard/WorkBreakdownPieChart.vue';
import { States, useStoreApiAction } from '@/composables/useStoreApiAction';
import { australianCurrency, cumulativeSum, roundDecimals } from '@/helpers';
import { ClaimStatus } from '@/models/Claim';
import { DelayStatus } from '@/models/Delay';
import { VariationOrderStatus } from '@/models/VariationOrder';
import { useClaimsStore } from '@/store/claims';
import { useDelaysStore } from '@/store/delays';
import { useProjectsStore } from '@/store/projects';
import { useTradesStore } from '@/store/trades';
import { useUserStore } from '@/store/user';
import { useVariationOrdersStore } from '@/store/variationOrders';
import { DateTime } from 'luxon';
import { computed, ref, watch } from 'vue';

const projectsStore = useProjectsStore();

const project = computed(() => {
  return projectsStore.project;
});

// Track hovered index for pie chart synchronization
const hoveredIndex = ref<number | undefined>(undefined);

const handleTimelineHover = (index: number | undefined) => {
  hoveredIndex.value = index;
};

const claimsStore = useClaimsStore();
const tradesStore = useTradesStore();
const variationOrdersStore = useVariationOrdersStore();
const delaysStore = useDelaysStore();
const fetchClaimsAction = useStoreApiAction(claimsStore.fetchClaims);
const fetchTradesAction = useStoreApiAction(tradesStore.fetchTrades);
const fetchVariationsAction = useStoreApiAction(variationOrdersStore.fetchVariationOrders);
const fetchDelaysAction = useStoreApiAction(delaysStore.fetchDelays);

watch(() => project.value?.id, (newValue) => {
  if(newValue) {
    fetchTradesAction.request(newValue, { boq_type: 'direct' }).catch((error) => {
      console.log(error);
    });

    fetchTradesAction.request(newValue, { boq_type: 'indirect' }).catch((error) => {
      console.log(error);
    });

    fetchClaimsAction.request(newValue, { boq_type: 'direct' }).catch((error) => {
      console.log(error);
    });

    fetchClaimsAction.request(newValue, { boq_type: 'indirect' }).catch((error) => {
      console.log(error);
    });

    fetchVariationsAction.request(newValue).catch((error) => {
      console.log(error);
    });

    fetchDelaysAction.request(newValue).catch((error) => {
      console.log(error);
    });
  }
}, { immediate: true });

const claims = computed(() => {
  return claimsStore.models.where('project_id', project.value?.id).where('boq_type', 'direct').get();
});

const trades = computed(() => {
  return tradesStore.models.where('project_id', project.value?.id).where('boq_type', 'direct').get();
});

const indirectTrades = computed(() => {
  return tradesStore.models.where('project_id', project.value?.id).where('boq_type', 'indirect').get();
});

const indirectClaims = computed(() => {
  return claimsStore.models.where('project_id', project.value?.id).where('boq_type', 'indirect').get();
});

const monthlyCertifiedCumulative = computed(() => {
  if(!project.value) {
    return [];
  }

  const claimSumPerMonth = new Map<string, number>();
  const lastClaim = claims.value.at(-1);

  if(lastClaim) {
    // Create extended months array that goes beyond contract end if needed
    const contractStart = DateTime.fromJSDate(project.value.contractStartDate).startOf('month');
    const lastClaimEnd = lastClaim.endDate.endOf('month');
    const contractEnd = DateTime.fromJSDate(project.value.contractEndDate).endOf('month');
    // Use the later of contract end or last claim end
    const chartEnd = lastClaimEnd > contractEnd ? lastClaimEnd : contractEnd;
    let currentMonth = contractStart;

    while(currentMonth <= chartEnd) {
      const key = currentMonth.toLocaleString({ month: '2-digit', year: 'numeric' });

      claimSumPerMonth.set(key, 0);
      currentMonth = currentMonth.plus({ months: 1 });
    }
  }

  claims.value.forEach((claim) => {
    const key = claim.startDate.toLocaleString({ month: '2-digit', year: 'numeric' });

    if(claimSumPerMonth.has(key)) {
      claimSumPerMonth.set(key, claimSumPerMonth.get(key) + claim.totalCertified);
    } else {
      claimSumPerMonth.set(key, claim.totalCertified);
    }
  });

  const cumulativeSum = ((sum) => (value: number) => sum += value)(0);

  return Array.from(claimSumPerMonth.values()).map(cumulativeSum);
});

// Helper computed to get extended chart months
const extendedChartMonths = computed(() => {
  if(!project.value) {
    return [];
  }

  const lastClaim = claims.value.at(-1);
  const contractStart = DateTime.fromJSDate(project.value.contractStartDate).startOf('month');
  const contractEnd = DateTime.fromJSDate(project.value.contractEndDate).endOf('month');

  if(lastClaim) {
    const lastClaimEnd = lastClaim.endDate.endOf('month');
    const chartEnd = lastClaimEnd > contractEnd ? lastClaimEnd : contractEnd;
    const months = [];
    let currentMonth = contractStart;

    while(currentMonth <= chartEnd) {
      months.push(currentMonth);
      currentMonth = currentMonth.plus({ months: 1 });
    }

    return months;
  }

  return project.value.contractMonths;
});

const baseline = computed(() => {
  if(!project.value) {
    return [];
  }

  // Use extended chart months instead of contract months
  const monthsInProject = extendedChartMonths.value;
  const monthlyTotals = [];

  monthsInProject.forEach((projectMonth, i) => {
    monthlyTotals[i] = 0;

    trades.value.filter((trade) => {
      return trade.baseline_start_date && trade.baseline_end_date;
    }).forEach((trade) => {
      if(trade.baselineStart <= projectMonth && projectMonth <= trade.baselineEnd) {
        monthlyTotals[i] += roundDecimals(
          trade.baseline_forecast[Math.round(Math.abs(trade.baselineStart.diff(projectMonth, ['months']).months))] ??
            0,
        );
      }
    });
  });

  return monthlyTotals;
});

const baselineCumulative = computed(() => {
  return cumulativeSum(baseline.value);
});

const forecast = computed(() => {
  if(!project.value) {
    return [];
  }

  // Use extended chart months instead of contract months
  const monthsInProject = extendedChartMonths.value;
  const monthlyTotals = [];

  monthsInProject.forEach((projectMonth, i) => {
    monthlyTotals[i] = 0;

    trades.value.filter((trade) => {
      return trade.forecast_start_date && trade.forecast_end_date && trade.forecast;
    }).forEach((trade) => {
      if(trade.forecastStart <= projectMonth && projectMonth <= trade.forecastEnd) {
        monthlyTotals[i] += roundDecimals(
          trade.forecast[Math.round(Math.abs(trade.forecastStart.diff(projectMonth, ['months']).months))] ??
            0,
        );
      }
    });
  });

  return monthlyTotals;
});

const forecastCumulative = computed(() => {
  return cumulativeSum(forecast.value);
});

const claimCertifiedTotal = computed(() => {
  return claims.value.reduce((total, claim) => {
    if(claim.totalCertified) {
      total += claim.totalCertified;
    }

    return total;
  }, 0);
});

// Current certified amount based on hovered index or last value
const currentCertifiedAmount = computed(() => {
  const index = hoveredIndex.value !== undefined ? hoveredIndex.value : (monthlyCertifiedCumulative.value.length - 1);

  return monthlyCertifiedCumulative.value[index] || 0;
});

const totalBudget = computed(() => {
  return trades.value.reduce((total, current) => {
    return total + current.totalAdjustedBudget;
  }, 0);
});

const approvedVariationOrders = computed(() => {
  return variationOrdersStore.models.where('project_id', project.value?.id).where(
    'status',
    VariationOrderStatus.approved,
  ).get();
});

const variationOrdersTotalApproved = computed(() => {
  return approvedVariationOrders.value
    .reduce((total, variationOrder) => {
      if(variationOrder.budget) {
        return total + variationOrder.budget;
      }

      return total;
    }, 0);
});

const approvedDelays = computed(() => {
  return delaysStore.models.where('project_id', project.value?.id).where(
    'status',
    DelayStatus.approved,
  ).get();
});

const approvedDelaysTotalDays = computed(() => {
  return approvedDelays.value.reduce((total, delay) => {
    return total + (delay.days || 0);
  }, 0);
});

const totalRetention = computed(() => {
  return claims.value
    .filter((claim) => claim.status === ClaimStatus.certified)
    .reduce((total, claim) => {
      return total + (claim.retention || 0);
    }, 0);
});

const userStore = useUserStore();

const hasSettingsAccess = computed(() => {
  return userStore.hasAccessToRoute('ProjectSettings');
});

const indirectTradesTotalBudget = computed(() => {
  return indirectTrades.value.reduce((total, trade) => {
    return total + trade.totalBudget;
  }, 0);
});

const indirectTradesTotalCertified = computed(() => {
  return indirectClaims.value.reduce((total, claim) => {
    return total + claim.totalCertified;
  }, 0);
});
</script>

<style scoped></style>
